/**
 * HTTP 请求封装模块
 * 
 * 基于 axios 封装全局请求配置和响应拦截器，统一处理接口调用和错误处理。
 * 所有前端 API 请求均通过此模块发送，确保请求配置一致性和错误处理统一。
 */
import axios from "axios";

/**
 * 创建 axios 实例并配置全局参数
 * 
 * @type {AxiosInstance} service - axios 实例
 * @property {string} baseURL - 请求基础路径，所有接口请求都会自动拼接此路径
 * @property {number} timeout - 请求超时时间，单位毫秒，超过15秒则超时
 * @property {boolean} withCredentials - 是否携带跨域凭证（Cookie），用于会话认证
 */
const service = axios.create({
  // 请求基础路径，后端 API 统一部署在 /api/v1 路径下
  baseURL: "/api/v1",
  // 请求超时时间：2分钟
  timeout: 120000,
  // 携带跨域凭证，用于 Session 认证机制
  withCredentials: true
});

/**
 * 请求拦截器
 * 
 * 在发送请求前添加管理员模拟登录信息。
 * 当管理员模拟用户或采集者登录时，在请求头中添加 X-Impersonate-User 或 X-Impersonate-Collector。
 */
service.interceptors.request.use(
  config => {
    const impersonateUser = localStorage.getItem('admin_impersonate_user');
    const impersonateCollector = localStorage.getItem('admin_impersonate_collector');
    
    if (impersonateUser) {
      try {
        const userInfo = JSON.parse(impersonateUser);
        config.headers['X-Impersonate-User'] = userInfo.id;
      } catch (e) {
        localStorage.removeItem('admin_impersonate_user');
      }
    } else if (impersonateCollector) {
      try {
        const collectorInfo = JSON.parse(impersonateCollector);
        config.headers['X-Impersonate-Collector'] = collectorInfo.id;
      } catch (e) {
        localStorage.removeItem('admin_impersonate_collector');
      }
    }
    
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

/**
 * 响应拦截器
 * 
 * 统一处理 API 响应：
 * 1. 成功响应：直接返回响应体 data 部分，简化调用方式
 * 2. 失败响应：提取错误信息，包装为 Error 对象抛出
 */
service.interceptors.response.use(
  /**
   * 成功响应处理
   * 
   * @param {AxiosResponse} response - axios 响应对象
   * @returns {any} 响应体 data 部分
   */
  response => response.data,
  
  /**
   * 失败响应处理
   * 
   * @param {Error} error - axios 错误对象
   * @returns {Promise<never>} 抛出包含错误信息的 Promise
   */
  error => {
    // 提取错误详情：error.response.data.detail.message
    const detail = error.response && error.response.data && error.response.data.detail;
    // 获取错误消息，若无法提取则使用默认消息
    const message = detail && detail.message ? detail.message : "请求失败";
    // 抛出 Error 对象，便于上层捕获和显示
    return Promise.reject(new Error(message));
  }
);

/**
 * 导出 axios 实例供其他模块使用
 */
export default service;
